import React from 'react'
import { FormikProps, useFormikContext } from 'formik'

import clsx from 'clsx'
import {
  BoxV4,
  RadioButtonGroupMapping,
  RadioButtonType,
  RadioButtonsV2,
  TextInputV4,
} from '@labrav/react-components'

import { Typography } from '@mui/material'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faRepeat } from '@fortawesome/pro-regular-svg-icons'

import { useStyles } from './RenewalSectionCombinedForm.styles'
import { ResaleAuthorizationWithErrors } from '../../../../modules/resaleAuthorization/types'
import { get } from 'lodash'
import { FieldMetadataField } from '../../../../modules/privateOffer/types'
import { FieldMetadata } from '../../../../modules/privateOffer/types'
import { useSelector } from '../../../../store'
import { renewalOptions } from '../../constants'
import type { RenewalSectionResellerProps } from '../../types'

export { renewalOptions } from '../../constants'
export type { RenewalSectionResellerProps }
export const RenewalSectionReseller: React.FC<RenewalSectionResellerProps> = ({
  product,
  disabled,
}) => {
  const {
    values,
    setFieldValue,
    errors,
    touched,
    handleChange,
    setFieldTouched,
    handleBlur,
  } = useFormikContext() as FormikProps<ResaleAuthorizationWithErrors>
  // error
  const renewalError = errors.renewal
  const renewalChannelError = errors.renewalChannel
  const renewalPricingModelError = errors?.renewalPricingModel
  // //touched
  const renewalTouched = touched?.renewal
  const renewalChannelTouched = touched?.renewalChannel
  const renewalPricingModelTouched = touched?.renewalPricingModel

  //is error check
  const hasRenewalError = renewalError && renewalTouched
  const hasRenewalChannelError = renewalChannelError && renewalChannelTouched
  const hasRenewalPricingError =
    renewalPricingModelError && renewalPricingModelTouched

  const fieldMetadata = useSelector<FieldMetadata | undefined>(
    state =>
      state.resaleAuthorizationsInCreation.currentResaleAuthorizationInCreation[
        product?.productId as string
      ]?.fieldMetadata
  )
  const isBuyerAwsAccountIdHidden = (
    fieldMetadata?.buyerAwsAccountId as FieldMetadataField
  )?.hidden
  const isBuyerAwsAccountIdReadOnly = (
    fieldMetadata?.buyerAwsAccountId as FieldMetadataField
  )?.readOnly
  const isRenewalChannelHidden = (
    fieldMetadata?.renewalChannel as FieldMetadataField
  )?.hidden
  const isRenewalChannelReadOnly = (
    fieldMetadata?.renewalChannel as FieldMetadataField
  )?.readOnly

  const { classes } = useStyles()
  const handleSelection = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = event.target.value
    if (value === 'Yes') {
      await setFieldValue('renewal', true)
      await setFieldValue('renewalChannel', 'EXTERNAL')
    } else if (value === 'No') {
      await setFieldValue('renewal', false)
      await setFieldValue('renewalChannel', null)
      await setFieldValue('renewalPricingModel', null)
    } else {
      await setFieldValue('renewal', '')
      await setFieldValue('renewalChannel', null)
      await setFieldValue('renewalPricingModel', null)
    }
    setFieldTouched('renewal', true)
  }
  const handleSelectionRenewalType = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = event.target.value
    await setFieldTouched('renewalChannel', true)
    await setFieldValue('renewalChannel', value)
  }

  const handleSelectionPricingModalType = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = event.target.value
    setFieldTouched('renewalPricingModel', true)
    setFieldValue('renewalPricingModel', value)
  }
  const checkedValue = () =>
    values.renewal ? 'Yes' : values.renewal === false ? 'No' : ''
  const buyerAwsAccountIdLabel = `Buyer AWS account ID${values.renewal === false ? ' (Optional)' : ''}`
  const channelTypes: RadioButtonGroupMapping[] = [
    {
      key: 'EXTERNAL',
      label: 'Existing customer moving to AWS Marketplace',
      type: RadioButtonType.STANDARD,
      infoDescription:
        'An existing customer you have been doing business with, and the next deal is migrating to AWS Marketplace as the transaction vehicle.',
      disabled: disabled,
    },
    {
      key: 'AWS_MARKETPLACE',
      label: 'Existing customer on AWS Marketplace',
      type: RadioButtonType.STANDARD,
      infoDescription:
        'Buyers are getting an offer to renew an existing agreement created on AWS Marketplace.',
      disabled: disabled,
    },
  ]
  const PricingModal: RadioButtonGroupMapping[] = [
    {
      key: 'Contract',
      label: 'Contract',
      type: RadioButtonType.STANDARD,
      infoDescription:
        'The customer entered into a contract with you entitling them to a specified quantity of use for an agreed-upon length of time',
      disabled: disabled,
    },
    {
      key: 'Usage',
      label: 'Usage',
      type: RadioButtonType.STANDARD,
      infoDescription:
        'The customer was billed for their usage of your product based on the per-unit price you set.',
      disabled: disabled,
    },

    {
      key: 'Byol',
      label: 'Bring your own license',
      type: RadioButtonType.STANDARD,
      infoDescription:
        'The customer was entitled to usage of your product by supplying a valid license key purchased outside of AWS Marketplace.',
      disabled: disabled,
    },
    {
      key: 'Free',
      label: 'Free',
      type: RadioButtonType.STANDARD,
      infoDescription:
        'The customer was entitled to usage of your product at no charge, either through a free trial or a $0 subscription.',
      disabled: disabled,
    },
  ]
  return (
    <div data-testid="renewal-section">
      <BoxV4
        heading={'Renewal'}
        headingIcon={
          <FontAwesomeIcon
            icon={faRepeat}
            style={{ width: '18px', height: '14px' }}
          />
        }
        // secondaryActions={
        //   <HelpDrawer
        //     sectionName="combinedFormRenewal"
        //     headerTitle="Help & FAQs"
        //   />
        // }
      >
        <div className={classes.sectionContainer}>
          <Typography
            className={clsx(classes.sectionText, classes.renewalChannelMragin)}
          >
            Is this reseller authorization intended to renew an existing paid
            subscription with an existing customer for the same underlying
            product?
          </Typography>
          <div className={classes.gapError}>
            <div className={classes.widthRadio}>
              <RadioButtonsV2
                checkedValue={{
                  key: checkedValue(),
                  value: checkedValue(),
                }}
                name={'renewal'}
                handleChange={handleSelection}
                radioButtonMappings={renewalOptions.map(option => ({
                  ...option,
                  disabled:
                    disabled ||
                    isRenewalChannelReadOnly ||
                    isRenewalChannelHidden,
                }))}
              />
            </div>
            {hasRenewalError && (
              <Typography className={classes.partnerTypeError}>
                {renewalError}
              </Typography>
            )}
          </div>
        </div>
        {!isBuyerAwsAccountIdHidden && (
          <div data-testid="renewal-type" className={classes.sectionContainer}>
            <Typography
              className={clsx(classes.sectionHeading, classes.textFieldMargin)}
            >
              Buyer AWS account ID
            </Typography>
            <div className={classes.inputWidth}>
              <TextInputV4
                keyName="buyerAwsAccountId"
                title={buyerAwsAccountIdLabel}
                type="text"
                placeHolder=""
                value={values.buyerAwsAccountId as string}
                maxNumber={12}
                isError={
                  !!(
                    get(touched, 'buyerAwsAccountId', false) &&
                    get(errors, 'buyerAwsAccountId', false)
                  )
                }
                onChange={handleChange}
                isReadOnly={isBuyerAwsAccountIdReadOnly || false}
                isRequired={false}
                errorText={
                  (!!get(touched, 'buyerAwsAccountId', false) &&
                    get(errors, 'buyerAwsAccountId', ' ')) ||
                  ' '
                }
                showSpinner={false}
                onBlur={handleBlur}
              />
            </div>
          </div>
        )}
        {!isRenewalChannelHidden && values.renewal && (
          <div data-testid="renewal-type" className={classes.sectionContainer}>
            <Typography
              className={clsx(
                classes.sectionHeading,
                classes.renewalChannelMragin
              )}
            >
              Renewal type
            </Typography>
            <div className={classes.gapError}>
              <div className={classes.radioButton}>
                <RadioButtonsV2
                  checkedValue={{
                    key: 'channel',
                    value: values.renewalChannel || '',
                  }}
                  name={'renewalChannel'}
                  handleChange={handleSelectionRenewalType}
                  radioButtonMappings={channelTypes.map(option => ({
                    ...option,
                    disabled: isRenewalChannelReadOnly,
                  }))}
                  vertical
                />
              </div>
              {hasRenewalChannelError && (
                <Typography className={classes.partnerTypeError}>
                  {renewalChannelError}
                </Typography>
              )}
            </div>
          </div>
        )}
        {values.renewal && (
          <div className={classes.sectionContainer}>
            <Typography
              className={clsx(
                classes.sectionHeading,
                classes.renewalChannelMragin
              )}
            >
              Previous agreement pricing model
            </Typography>
            <div className={classes.gapError}>
              <div className={classes.radioWithRadio}>
                <RadioButtonsV2
                  checkedValue={{
                    key: 'channel',
                    value: values.renewalPricingModel || '',
                  }}
                  name={'renewalPricingModel'}
                  handleChange={handleSelectionPricingModalType}
                  radioButtonMappings={PricingModal}
                />
              </div>
              {hasRenewalPricingError && (
                <Typography className={classes.partnerTypeError}>
                  {renewalPricingModelError}
                </Typography>
              )}
            </div>
          </div>
        )}
      </BoxV4>
    </div>
  )
}
